/**
 * Advantages section component - using original Portugal Portuguese text from old website
 */

import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { motion } from 'framer-motion';
import advantagesData from '@/data/advantages.json';

export function Advantages() {
  const { ref, isIntersecting } = useIntersectionObserver({ threshold: 0.1 });
  const sectionTitleParts = advantagesData.section_title_parts || [{ text: advantagesData.section_title, highlight: false }];

  return (
    <section id="advantages" ref={ref} className="section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="has-text-centered mb-6"
        >
          <h2 className="title is-2 has-text-weight-bold">
            {sectionTitleParts.map((part, index) => (
              part.highlight ? (
                <span key={index} className="gradient-text">{part.text}</span>
              ) : (
                <span key={index}>{part.text}</span>
              )
            ))}
          </h2>
          <p className="subtitle is-5" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {advantagesData.section_subtitle}
          </p>
        </motion.div>

        <div className="columns is-vcentered mb-6">
          {/* Image */}
          <div className="column is-6 order-2-desktop order-1">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isIntersecting ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <figure className="image card-magreen">
                <img
                  src={advantagesData.image}
                  alt={advantagesData.image_alt}
                  style={{ borderRadius: '1rem' }}
                />
              </figure>
            </motion.div>
          </div>

          {/* List */}
          <div className="column is-6 order-1-desktop order-2">
            {advantagesData.items.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, x: 30 }}
                animate={isIntersecting ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}
              >
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  background: 'linear-gradient(135deg, var(--color-lime-50), var(--color-lime-100))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className={`fas ${advantage.icon}`} style={{ color: 'var(--color-lime-600)', fontSize: '1.25rem' }}></i>
                </div>
                <div>
                  <h3 className="title is-5 has-text-weight-semibold mb-2" style={{ color: 'var(--color-gray-800)' }}>
                    {advantage.title}
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)' }}>{advantage.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Guarantee Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isIntersecting ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card-magreen has-text-centered p-6"
          style={{
            background: 'linear-gradient(to right, var(--color-lime-500), var(--color-lime-600))',
            color: '#ffffff',
            borderRadius: '1rem',
            padding: '3rem'
          }}
        >
          <i className="fas fa-certificate" style={{ fontSize: '3rem', marginBottom: '1.5rem', opacity: 0.9 }}></i>
          <h3 className="title is-2 has-text-weight-bold mb-4" style={{ color: '#ffffff' }}>
            {advantagesData.guarantee_title}
          </h3>
          <p className="subtitle is-5 mb-6" style={{ maxWidth: '700px', margin: '0 auto 1.5rem', color: 'rgba(255, 255, 255, 0.95)' }}>
            {advantagesData.guarantee_description}
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                const top = element.getBoundingClientRect().top + window.scrollY - 80;
                window.scrollTo({ top, behavior: 'smooth' });
              }
            }}
            className="button"
            style={{
              background: '#ffffff',
              color: 'var(--color-lime-600)',
              borderRadius: '9999px',
              padding: '1rem 2rem',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-100)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {advantagesData.guarantee_cta}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
